import { useState, useEffect, useCallback } from 'react';
import { ChatMessage, CreateMessageData } from '@/types';
import { subscribeToMessages, sendMessage, markExchangeMessagesAsRead } from '@/services';

export interface UseChatReturn {
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<void>;
  markAsRead: () => Promise<void>;
}

/**
 * Custom hook for chat functionality
 */
export const useChat = (
  exchangeId: string | null,
  userId?: string,
  userName?: string
): UseChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!exchangeId) {
      setMessages([]);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToMessages(exchangeId, (newMessages) => {
      setMessages(newMessages);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [exchangeId]);

  const handleSendMessage = async (message: string): Promise<void> => {
    if (!exchangeId || !userId || !userName) {
      throw new Error('Missing required data for sending message');
    }

    try {
      setError(null);

      const messageData: CreateMessageData = {
        exchangeId,
        senderId: userId,
        senderName: userName,
        message: message.trim(),
      };

      await sendMessage(messageData);
    } catch (err) {
      setError('Failed to send message');
      console.error('Error sending message:', err);
      throw err;
    }
  };

  const markAsRead = async (): Promise<void> => {
    if (!exchangeId || !userId) return;

    try {
      await markExchangeMessagesAsRead(exchangeId, userId);
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  return {
    messages,
    loading,
    error,
    sendMessage: handleSendMessage,
    markAsRead,
  };
};
